import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase'; 
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// --- Import All Your Pages ---
import Home from './components/Home';
import PartnerDashboard from './components/PartnerDashboard';
import PartnerSetup from './components/PartnerSetup';
import ClientDashboard from './components/ClientDashboard';
import StaffDashboard from './components/StaffDashboard';
import SocialFeed from './components/SocialFeed';
import ShopDetail from './components/ShopDetail'; // Make sure ShopDetail is imported!
import HowItWorks from './components/HowItWorks';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedShop, setSelectedShop] = useState(null); 
  const [navData, setNavData] = useState(null); // <-- NEW: Remembers tab data
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Global Dark Mode State 
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    // Listen for Firebase Login/Logout changes
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", authUser.uid));
          if (userDoc.exists()) {
            setUser({ uid: authUser.uid, ...userDoc.data() });
          } else {
            setUser({ uid: authUser.uid, email: authUser.email }); 
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUser(null);
        setCurrentView('home'); 
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

// --- THE UPDATED NAVIGATION ENGINE ---
  const handleNavigate = (view, data = null) => {
    if (view === 'detail') setSelectedShop(data); 
    setNavData(data); // <-- NEW: Save the specific data being passed
    
    window.history.pushState({ view, data }, '', `#${view}`);
    setCurrentView(view);
  };

  // --- NEW: Browser Back Button Listener ---
  useEffect(() => {
    window.history.replaceState({ view: 'home', data: null }, '', '#home');

    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        setCurrentView(event.state.view);
        if (event.state.view === 'detail' && event.state.data) {
          setSelectedShop(event.state.data);
        }
      } else {
        setCurrentView('home'); 
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a', color: 'white' }}>
        <h2>Loading BookMyStyle...</h2>
      </div>
    );
  }

  // --- ROUTING LOGIC (Notice how they ALL use handleNavigate now) ---
  
  if (currentView === 'how-it-works') {
    // Passes 'client', 'staff', or 'partner' so the page knows what to show
    return <HowItWorks role={navData} onNavigate={handleNavigate} darkMode={darkMode} />;
  }
  if (currentView === 'detail') {
    // This passes the remembered shop into the Detail page
    return <ShopDetail shop={selectedShop} user={user} onNavigate={handleNavigate} darkMode={darkMode} />;
  }

  if (currentView === 'social') {
    return <SocialFeed onNavigate={handleNavigate} darkMode={darkMode} />;
  }

  if (currentView === 'client-dashboard') {
    return <ClientDashboard onNavigate={handleNavigate} user={user} />;
  }

  if (currentView === 'partner-setup') {
    return <PartnerSetup onComplete={() => handleNavigate('partner-dashboard')} user={user} />;
  }

  if (currentView === 'partner-dashboard') {
    return <PartnerDashboard onNavigate={handleNavigate} user={user} />;
  }

  if (currentView === 'staff-dashboard') {
    return <StaffDashboard onNavigate={handleNavigate} user={user} />;
  }

  // Fallback catch-all
  return <Home onNavigate={handleNavigate} user={user} darkMode={darkMode} setDarkMode={setDarkMode} />;
}

export default App;